/**
 * UI Redux Slice - Global UI state management
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  actions?: Array<{
    label: string;
    action: () => void;
    style?: 'primary' | 'secondary';
  }>;
}

interface Modal {
  id: string;
  component: string;
  props?: Record<string, any>;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closable?: boolean;
}

interface UIState {
  // Theme and Appearance
  theme: 'light' | 'dark' | 'auto';
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;

  // Loading States
  globalLoading: boolean;
  loadingMessage?: string;

  // Notifications
  notifications: Notification[];

  // Modals
  modals: Modal[];

  // Search
  globalSearchOpen: boolean;
  globalSearchQuery: string;

  // Navigation
  breadcrumbs: Array<{
    label: string;
    href?: string;
    current?: boolean;
  }>;

  // Page State
  pageTitle: string;
  pageDescription?: string;

  // Responsive
  isMobile: boolean;
  isTablet: boolean;
}

const initialState: UIState = {
  theme: 'auto',
  sidebarOpen: true,
  sidebarCollapsed: false,

  globalLoading: false,

  notifications: [],
  modals: [],

  globalSearchOpen: false,
  globalSearchQuery: '',

  breadcrumbs: [],

  pageTitle: 'Relay Protocol',

  isMobile: false,
  isTablet: false
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Theme
    setTheme: (state, action: PayloadAction<'light' | 'dark' | 'auto'>) => {
      state.theme = action.payload;
      localStorage.setItem('theme', action.payload);
    },

    // Sidebar
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },

    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },

    toggleSidebarCollapsed: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },

    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload;
    },

    // Loading
    setGlobalLoading: (state, action: PayloadAction<{ loading: boolean; message?: string }>) => {
      state.globalLoading = action.payload.loading;
      state.loadingMessage = action.payload.message;
    },

    // Notifications
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const notification: Notification = {
        id: `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        duration: 5000, // 5 seconds default
        ...action.payload
      };

      state.notifications.push(notification);

      // Auto-remove notification after duration (handled by middleware or component)
    },

    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        notification => notification.id !== action.payload
      );
    },

    clearNotifications: (state) => {
      state.notifications = [];
    },

    // Modals
    openModal: (state, action: PayloadAction<Omit<Modal, 'id'>>) => {
      const modal: Modal = {
        id: `modal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        size: 'md',
        closable: true,
        ...action.payload
      };

      state.modals.push(modal);
    },

    closeModal: (state, action: PayloadAction<string>) => {
      state.modals = state.modals.filter(modal => modal.id !== action.payload);
    },

    closeTopModal: (state) => {
      if (state.modals.length > 0) {
        state.modals.pop();
      }
    },

    closeAllModals: (state) => {
      state.modals = [];
    },

    // Global Search
    setGlobalSearchOpen: (state, action: PayloadAction<boolean>) => {
      state.globalSearchOpen = action.payload;
      if (!action.payload) {
        state.globalSearchQuery = '';
      }
    },

    setGlobalSearchQuery: (state, action: PayloadAction<string>) => {
      state.globalSearchQuery = action.payload;
    },

    // Breadcrumbs
    setBreadcrumbs: (state, action: PayloadAction<UIState['breadcrumbs']>) => {
      state.breadcrumbs = action.payload;
    },

    addBreadcrumb: (state, action: PayloadAction<UIState['breadcrumbs'][0]>) => {
      // Remove 'current' from existing breadcrumbs
      state.breadcrumbs = state.breadcrumbs.map(breadcrumb => ({
        ...breadcrumb,
        current: false
      }));

      // Add new breadcrumb as current
      state.breadcrumbs.push({
        ...action.payload,
        current: true
      });
    },

    // Page Info
    setPageTitle: (state, action: PayloadAction<string>) => {
      state.pageTitle = action.payload;
      document.title = action.payload;
    },

    setPageDescription: (state, action: PayloadAction<string | undefined>) => {
      state.pageDescription = action.payload;
    },

    setPageInfo: (state, action: PayloadAction<{ title: string; description?: string }>) => {
      state.pageTitle = action.payload.title;
      state.pageDescription = action.payload.description;
      document.title = action.payload.title;
    },

    // Responsive
    setResponsiveState: (state, action: PayloadAction<{ isMobile: boolean; isTablet: boolean }>) => {
      state.isMobile = action.payload.isMobile;
      state.isTablet = action.payload.isTablet;

      // Auto-collapse sidebar on mobile
      if (action.payload.isMobile) {
        state.sidebarOpen = false;
      }
    }
  }
});

export const {
  // Theme
  setTheme,

  // Sidebar
  toggleSidebar,
  setSidebarOpen,
  toggleSidebarCollapsed,
  setSidebarCollapsed,

  // Loading
  setGlobalLoading,

  // Notifications
  addNotification,
  removeNotification,
  clearNotifications,

  // Modals
  openModal,
  closeModal,
  closeTopModal,
  closeAllModals,

  // Global Search
  setGlobalSearchOpen,
  setGlobalSearchQuery,

  // Breadcrumbs
  setBreadcrumbs,
  addBreadcrumb,

  // Page Info
  setPageTitle,
  setPageDescription,
  setPageInfo,

  // Responsive
  setResponsiveState
} = uiSlice.actions;

// Notification helper actions
export const showSuccessNotification = (title: string, message?: string) =>
  addNotification({ type: 'success', title, message });

export const showErrorNotification = (title: string, message?: string) =>
  addNotification({ type: 'error', title, message });

export const showWarningNotification = (title: string, message?: string) =>
  addNotification({ type: 'warning', title, message });

export const showInfoNotification = (title: string, message?: string) =>
  addNotification({ type: 'info', title, message });

export default uiSlice.reducer;